// ==================== DASHBOARD PAGE ====================
// src/pages/Dashboard.tsx
import React, { useEffect, useState } from "react";
import { api } from "../api/client";
import { TrendingUp, Users, CheckCircle, AlertCircle } from "lucide-react";

type SessionSummary = {
  id: string;
  sprintLabel: string;
  itemCount: number;
  actionCount: number;
  createdAt: string;
};

type AttentionAction = {
  id: string;
  text: string;
  ownerName?: string;
  dueDate?: string;
  status?: string;
  isOverdue?: boolean;
};

type DashboardStats = {
  totalRetros: number;
  activeParticipants: number;
  completedActions: number;
  completionRate: number;
  overdueActions: number;
  recentSessions: SessionSummary[];
  attentionActions: AttentionAction[];
};

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    try {
      const data = await api.getDashboard();
      setStats(data);
    } catch (error) {
      console.error("Failed to load dashboard", error);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-8">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Retros"
          value={stats?.totalRetros || 0}
          icon={<TrendingUp className="text-blue-600" />}
          trend="+12% from last month"
        />
        <StatCard
          title="Active Participants"
          value={stats?.activeParticipants || 0}
          icon={<Users className="text-green-600" />}
          trend="Across all teams"
        />
        <StatCard
          title="Completed Actions"
          value={stats?.completedActions || 0}
          icon={<CheckCircle className="text-purple-600" />}
          trend={`${stats?.completionRate || 0}% completion rate`}
        />
        <StatCard
          title="Overdue Actions"
          value={stats?.overdueActions || 0}
          icon={<AlertCircle className="text-red-600" />}
          trend="Need attention"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Recent Sessions">
          <div className="space-y-3">
            {stats?.recentSessions?.map((session) => (
              <SessionItem key={session.id} session={session} />
            ))}
          </div>
        </Card>

        <Card title="Action Items Needing Attention">
          <div className="space-y-3">
            {stats?.attentionActions?.map((action) => (
              <ActionItem key={action.id} action={action} />
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  trend,
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  trend: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        {icon}
      </div>
      <div className="text-3xl font-bold mb-1">{value}</div>
      <div className="text-sm text-gray-500">{trend}</div>
    </div>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function SessionItem({ session }: { session: SessionSummary }) {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
      <div>
        <div className="font-medium">{session.sprintLabel}</div>
        <div className="text-sm text-gray-600">
          {session.itemCount} items • {session.actionCount} actions
        </div>
      </div>
      <div className="text-sm text-gray-500">
        {new Date(session.createdAt).toLocaleDateString()}
      </div>
    </div>
  );
}

function ActionItem({ action }: { action: AttentionAction }) {
  return (
    <div className="p-3 bg-gray-50 rounded">
      <div className="font-medium mb-1">{action.text}</div>
      <div className="text-sm text-gray-600">
        Owner: {action.ownerName} • Due: {action.dueDate}
        {action.status === "blocked" && (
          <span className="ml-2 text-red-600">• Blocked</span>
        )}
        {action.isOverdue && (
          <span className="ml-2 text-orange-600">• Overdue</span>
        )}
      </div>
    </div>
  );
}
